/**
 * A readable message out of a tRPC error.
 *
 * Input-validation failures arrive with `message` set to a JSON dump of the whole ZodError
 * — several lines of `{"code":"custom","path":[...]}` — which is unusable in a toast and
 * breaks the rule that an error should say WHERE. The server keys `zodError.fieldErrors` by
 * the full dotted issue path (`overrides.1.set.gameName`) precisely so this can be turned
 * back into a sentence.
 */
export type ZodErrorShape = { formErrors: string[]; fieldErrors: Record<string, string[]> };

/**
 * Structurally typed rather than via `TRPCClientErrorLike`: that type is generic over the
 * router and resolves `data` to `never` at this call site, where the error is a bare
 * `unknown` from a query cache callback.
 */
export type MaybeTrpcError = { message?: string; data?: { zodError?: ZodErrorShape | null; code?: string } | null };

/** The zod issues a tRPC error carries, or `null` for anything else — `components/Editor/issues.ts` turns them into field messages. */
export const zodErrorOf = (error: unknown): ZodErrorShape | null =>
    (error as MaybeTrpcError | undefined)?.data?.zodError ?? null;

/** The tRPC error code (`NOT_FOUND`, `CONFLICT`, …), or `null` for anything else. */
export const codeOf = (error: unknown): string | null => (error as MaybeTrpcError | undefined)?.data?.code ?? null;

export const messageOf = (error: unknown): string | undefined => (error as MaybeTrpcError | undefined)?.message;

export const errorMessage = (error: unknown): string => {
    const zodError = zodErrorOf(error);

    if (zodError) {
        const fields = Object.entries(zodError.fieldErrors)
            .map(([path, messages]) => `${path}: ${messages?.[0] ?? 'invalid'}`)
            .slice(0, 3);
        const parts = [...zodError.formErrors, ...fields].filter(Boolean);
        if (parts.length > 0) return parts.join(' · ');
    }

    const message = messageOf(error);
    if (!message) return 'Something went wrong';

    // A dump that slipped through anyway: better a generic sentence than five lines of JSON.
    return message.trimStart().startsWith('[') || message.trimStart().startsWith('{')
        ? 'The request was rejected as invalid'
        : message;
};
